/**
 * CRM 国内/海外模式解析 (M68)
 *
 * 统一替代分散在各组件中的 isOverseas 判断逻辑:
 *   - AuthUser.isOverseas (BU 树递归)
 *   - buName.includes('海外') (字符串匹配, 脆弱)
 *   - [1,2,3].includes(marketsegment) (间接推断)
 *
 * 优先级: entityMode > 角色模式偏好 > AuthUser.isOverseas > fallback 'domestic'
 */

import { useGetIdentity } from '@refinedev/core';
import { useEffect, useState } from 'react';
import type { AuthUser } from './auth-provider.js';
import { USER_KEY } from './client.js';
import type { CrmMode } from './crm-mode-config.js';
import { CRM_MODE_CHANGED_EVENT, resolveUserCrmMode } from './crm-mode-preference.js';

export const OVERSEAS_CUSTOMER_DEPARTMENTS = [2, 5] as const;
export const DOMESTIC_CUSTOMER_DEPARTMENTS = [1, 3, 4, 6, 7, 8] as const;

interface UseCrmModeOptions {
  /**
   * 实体级模式覆盖。当实体自身有明确的国内/海外标识时,
   * 优先使用实体级模式而非用户级模式。
   *
   * 传 null 表示无实体级覆盖,回退到用户级。
   */
  entityMode?: CrmMode | null;
}

/**
 * Hook: 解析当前 CRM 模式
 *
 * @example
 *   // 场景 A: 新建表单 (用户级模式)
 *   const mode = useCrmMode();
 *
 *   // 场景 B: 编辑已有记录 (实体级模式)
 *   const mode = useCrmMode({ entityMode: modeFromDomesticOverseas(row.iws_domestic_overseas) });
 */
export function useCrmMode(options?: UseCrmModeOptions): CrmMode {
  const { data: user } = useGetIdentity<AuthUser>();
  useCrmModePreferenceSignal();

  // 实体级优先
  if (options?.entityMode) return options.entityMode;

  // 用户级: 支持同一用户拥有国内+海外角色时手动切换
  return resolveUserCrmMode(user);
}

/**
 * 非 hook 版本 — 适用于 React 组件外的纯函数场景。
 * 从 localStorage 读取用户上下文。
 */
export function getCrmMode(): CrmMode {
  return resolveUserCrmMode(readStoredUser());
}

function useCrmModePreferenceSignal(): number {
  const [signal, setSignal] = useState(0);

  useEffect(() => {
    const bump = () => setSignal((prev) => prev + 1);
    window.addEventListener(CRM_MODE_CHANGED_EVENT, bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener(CRM_MODE_CHANGED_EVENT, bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  return signal;
}

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

/**
 * 从 iws_customerbelongsdepartment 推导模式
 *   海外市场部/网络产品事业部 → 'overseas'
 *   国内市场部/国内公网/国内专网/空天智联/科创部/行业方案部 → 'domestic'
 *   其他/null → null (回退到用户级)
 */
export function modeFromDepartment(dept: number | null | undefined): CrmMode | null {
  if (dept != null && (OVERSEAS_CUSTOMER_DEPARTMENTS as readonly number[]).includes(dept)) {
    return 'overseas';
  }
  if (dept != null && (DOMESTIC_CUSTOMER_DEPARTMENTS as readonly number[]).includes(dept)) {
    return 'domestic';
  }
  return null;
}

/**
 * 从 iws_domestic_overseas 推导模式。
 * 新建客户按创建人当前模式过滤选项；已有客户优先用该明确字段决定国内/海外模式。
 */
export function modeFromDomesticOverseas(value: unknown): CrmMode | null {
  if (value === 'domestic' || value === 1 || value === '1') return 'domestic';
  if (value === 'overseas' || value === 2 || value === '2') return 'overseas';
  return null;
}

/**
 * 从客户分类兜底推导模式。
 * 1=OEM、9999=其他为共享项，不单独推导国内/海外。
 */
export function modeFromMarketSegment(value: number | null | undefined): CrmMode | null {
  if (value != null && [2, 3, 4].includes(value)) return 'overseas';
  if (value != null && [5, 6, 7, 8, 9, 10, 11, 12].includes(value)) return 'domestic';
  return null;
}

/**
 * 兼容旧调用点: iws_salestype 已改为销售模式，不再推导国内/海外。
 */
export function modeFromSalesType(salesType: number | null | undefined): CrmMode | null {
  void salesType;
  return null;
}

// ── 默认货币 (D365 业务规则: 海外 → USD, 国内 → CNY) ──────────────────

/** UUID 常量 — 与 D365 migration seed 的 transactioncurrency 表对齐 */
export const CNY_CURRENCY_ID = 'c06c88fc-01ec-ed11-8849-6045bd20a09e';
export const USD_CURRENCY_ID = '5decf99d-8d00-ee11-8f6c-000d3ac71cd1';
export const EUR_CURRENCY_ID = '870750cd-152d-ee11-9965-000d3ac7140c';
export const AUD_CURRENCY_ID = '8455d0fd-0f46-ee11-be6d-6045bd1d78cf';
export const GBP_CURRENCY_ID = '0cd2eb24-29ed-489d-91f9-40828be9e552';
export const JPY_CURRENCY_ID = '868bbd80-c61b-40b4-9854-874eda8b4601';
export const INR_CURRENCY_ID = '4e946aa2-e9cc-4b23-b354-283e76717ed8';
export const KRW_CURRENCY_ID = '6c575a03-1794-45b4-8d99-55ebd86b333d';
export const SGD_CURRENCY_ID = '0fee4067-2bb8-4f79-9466-346a0a9b622e';

/**
 * 按 CRM 模式返回默认货币 UUID
 *   overseas → USD_CURRENCY_ID
 *   domestic → CNY_CURRENCY_ID
 */
export function getDefaultCurrencyId(mode: CrmMode): string {
  return mode === 'overseas' ? USD_CURRENCY_ID : CNY_CURRENCY_ID;
}
